import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus,
  Calendar,
  Clock,
  Trash2,
  Check,
  Search,
  ChevronDown,
  ChevronUp,
  Building2,
  CalendarDays,
  ArrowRight,
  ArrowLeft,
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type {
  WizardStepProps,
  ClubScheduleSelection,
  ScheduleSelectionSlice,
} from '../../../engine/types';
import type { Club } from '@/types/tournament.model';
import { getClubsByOwner, getClubsByManager } from '@/api/clubs/clubApi';
import { CLUB_FIELDS } from '@/api/clubs/clubs-fields.config';
import { CLUB_STATUS } from '@/types/club.model';
import {
  isLocalDateAfter,
  minLocalMidnightDate,
  toLocalMidnightDate,
} from '@/utils/localDateOnly';
import { getSchedulesByClub, createSchedule } from '@/api/schedule/scheduleApi';
import type {
  Schedule as ApiSchedule,
  CreateScheduleRequest,
  ScheduleTimeRange,
  ScheduleEntry,
} from '@/types/schedule.model';
import { getDayOfWeek } from '@/types/schedule.model';
import { handleGlobalError } from '@/utils/error-handler';
import { getCourtsCountForSport } from '@/utils/club-utils';
import { DateInputWithCalendar } from '@/components/ui/date-input-with-calendar';

// ============================================================================
// INTERNAL TYPES
// ============================================================================

interface TimeBlock {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  courtCount: number;
}

interface Schedule {
  id: string;
  title: string;
  clubId: string;
  clubName: string;
  clubAddress: string;
  timeBlocks: TimeBlock[];
  isReusable: boolean;
  createdAt: string;
}

interface ClubWithSchedules {
  id: string;
  name: string;
  address: string;
  maxCourts: number;
}

// ============================================================================
// INTERNAL STEP TYPE
// ============================================================================

type InternalStep = 'tournament-config' | 'clubs-schedules';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getScheduleStartDate(schedule: Schedule): Date | null {
  return minLocalMidnightDate(schedule.timeBlocks.map((b) => b.date));
}

function isScheduleStartAfterDeadline(
  schedule: Schedule,
  signupDeadlineDate: Date | string | null | undefined,
): boolean {
  if (!signupDeadlineDate) return true;
  const start = getScheduleStartDate(schedule);
  if (!start) return false;
  const deadline = toLocalMidnightDate(signupDeadlineDate);
  if (!deadline) return true;
  return isLocalDateAfter(start, deadline) === true;
}

function transformApiScheduleToUI(apiSchedule: ApiSchedule): Schedule {
  const timeBlocks: TimeBlock[] = [];
  let blockIdCounter = 0;

  const scheduleEntries = apiSchedule.schedule || [];

  scheduleEntries.forEach((entry) => {
    entry.timeRange.forEach((range) => {
      timeBlocks.push({
        id: `tb-${apiSchedule.id}-${blockIdCounter++}`,
        date: entry.date,
        startTime: range.startTime,
        endTime: range.endTime,
        courtCount: range.courtsQuantity,
      });
    });
  });

  const club = apiSchedule.club;
  const clubAddress =
    club?.address?.street && club?.address?.city
      ? `${club.address.street}, ${club.address.city}`
      : club?.address?.street || '';

  return {
    id: apiSchedule.id,
    title: apiSchedule.name || 'Sin nombre',
    clubId: club?.id || '',
    clubName: club?.name || 'Club desconocido',
    clubAddress,
    timeBlocks,
    isReusable: true,
    createdAt: apiSchedule.createdAt || new Date().toISOString(),
  };
}

function transformTimeSlotsToApiFormat(
  timeSlots: TimeSlotSelection,
  availableSlots: { startTime: string; endTime: string }[],
): ScheduleEntry[] {
  const entries: ScheduleEntry[] = [];

  Object.entries(timeSlots).forEach(([dateKey, slots]) => {
    const timeRanges: ScheduleTimeRange[] = [];

    Object.entries(slots).forEach(([timeKey, courtCount]) => {
      const slot = availableSlots.find((s) => s.startTime === timeKey);
      if (slot && courtCount > 0) {
        timeRanges.push({
          qty: courtCount,
          courtsQuantity: courtCount,
          startTime: slot.startTime,
          endTime: slot.endTime,
        });
      }
    });

    if (timeRanges.length > 0) {
      const date = new Date(dateKey + 'T00:00:00');
      entries.push({
        date: dateKey,
        dayOfWeek: getDayOfWeek(date),
        timeRange: timeRanges,
      });
    }
  });

  return entries;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ScheduleSelectionStep({
  data,
  updateData,
  draft,
}: WizardStepProps<ScheduleSelectionSlice>) {
  // Internal step navigation
  const [internalStep, setInternalStep] =
    useState<InternalStep>('tournament-config');

  // Clubs state
  const [availableClubs, setAvailableClubs] = useState<Club[]>([]);
  const [isLoadingClubs, setIsLoadingClubs] = useState(true);
  const [clubsError, setClubsError] = useState<string | null>(null);

  // Schedules cache
  const [schedulesCache, setSchedulesCache] = useState<
    Record<string, Schedule[]>
  >({});
  const [loadingClubSchedules, setLoadingClubSchedules] = useState<
    string | null
  >(null);

  // Get data
  const clubSchedules = data?.clubSchedules ?? [];
  const signupDeadlineDate = data?.signupDeadlineDate ?? null;
  const matchDuration = data?.matchDuration ?? 60;

  // Defensive normalization: if deadline changes (or schedules load), drop selections
  // whose schedule start date is <= signupDeadlineDate.
  const effectiveClubSchedules = useMemo(() => {
    return clubSchedules.map((cs) => {
      const schedules = schedulesCache[cs.clubId];
      if (schedules === undefined) return cs; // don't prune until we actually loaded schedules

      const validScheduleIds = schedules
        .filter((s) => isScheduleStartAfterDeadline(s, signupDeadlineDate))
        .map((s) => s.id);

      return {
        ...cs,
        scheduleIds: cs.scheduleIds.filter((id) =>
          validScheduleIds.includes(id),
        ),
      };
    });
  }, [clubSchedules, schedulesCache, signupDeadlineDate]);

  const hasInvalidSelections = useMemo(() => {
    return clubSchedules.some(
      (cs, i) =>
        cs.scheduleIds.length !==
          effectiveClubSchedules[i].scheduleIds.length ||
        cs.scheduleIds.some(
          (id, j) => id !== effectiveClubSchedules[i].scheduleIds[j],
        ),
    );
  }, [clubSchedules, effectiveClubSchedules]);

  useEffect(() => {
    if (hasInvalidSelections) {
      updateData({ clubSchedules: effectiveClubSchedules });
    }
  }, [hasInvalidSelections, effectiveClubSchedules, updateData]);

  // Calculate totals
  const totalSchedules = effectiveClubSchedules.reduce(
    (acc, cs) => acc + cs.scheduleIds.length,
    0,
  );

  // Check if tournament config is complete
  const isTournamentConfigComplete = Boolean(
    signupDeadlineDate && matchDuration >= 30,
  );

  // Fetch clubs
  const fetchClubs = useCallback(async () => {
    setIsLoadingClubs(true);
    setClubsError(null);

    try {
      const [ownerClubs, managerClubs] = await Promise.allSettled([
        getClubsByOwner(CLUB_FIELDS.LIST),
        getClubsByManager(CLUB_FIELDS.LIST),
      ]);

      const ownerClubsResult =
        ownerClubs.status === 'fulfilled'
          ? (ownerClubs.value as unknown as Club[])
          : [];

      const managerClubsResult =
        managerClubs.status === 'fulfilled'
          ? (managerClubs.value as unknown as Club[])
          : [];

      const allClubs = [...ownerClubsResult, ...managerClubsResult];
      const validClubs = allClubs.filter((club) => {
        const isEnabled = club.status === CLUB_STATUS.ENABLED;
        const hasCourts = club.courts && club.courts.length >= 1;
        return isEnabled && hasCourts;
      });

      const uniqueClubs = validClubs.filter(
        (club, index, self) =>
          index === self.findIndex((c) => c.id === club.id),
      );

      setAvailableClubs(uniqueClubs);
    } catch (err) {
      setClubsError('No se pudieron cargar los clubs');
      handleGlobalError(err, 'Error', 'No se pudieron cargar los clubs');
    } finally {
      setIsLoadingClubs(false);
    }
  }, []);

  // Fetch schedules for a specific club
  const fetchSchedulesForClub = useCallback(
    async (clubId: string) => {
      if (schedulesCache[clubId]) return;

      setLoadingClubSchedules(clubId);

      try {
        const apiSchedules = await getSchedulesByClub(clubId);
        const schedulesArray = Array.isArray(apiSchedules) ? apiSchedules : [];
        const uiSchedules = schedulesArray.map((s) =>
          transformApiScheduleToUI(s),
        );

        setSchedulesCache((prev) => ({
          ...prev,
          [clubId]: uiSchedules,
        }));
      } catch (err) {
        console.error('Error fetching schedules:', err);
        handleGlobalError(
          err,
          'Error',
          'No se pudieron cargar los bloques de horario',
        );
      } finally {
        setLoadingClubSchedules(null);
      }
    },
    [schedulesCache],
  );

  // Initialize with defaults
  useEffect(() => {
    if (!data || data.matchDuration === undefined) {
      updateData({
        clubSchedules: data?.clubSchedules ?? [],
        signupDeadlineDate: data?.signupDeadlineDate ?? null,
        matchDuration: data?.matchDuration ?? 60,
      });
    }
  }, [data, updateData]);

  // Load clubs on mount
  useEffect(() => {
    fetchClubs();
  }, [fetchClubs]);

  // Load schedules for selected clubs
  useEffect(() => {
    clubSchedules.forEach((cs) => {
      if (!schedulesCache[cs.clubId]) {
        fetchSchedulesForClub(cs.clubId);
      }
    });
  }, [clubSchedules, schedulesCache, fetchSchedulesForClub]);

  // Determine initial step based on data
  useEffect(() => {
    if (isTournamentConfigComplete && clubSchedules.length > 0) {
      setInternalStep('clubs-schedules');
    }
  }, []);

  // Handlers
  const handleSignupDeadlineChange = (value: Date | null) => {
    updateData({ signupDeadlineDate: value });
  };

  const handleMatchDurationChange = (value: number) => {
    const clampedValue = Math.max(30, Math.min(value, 120));
    updateData({ matchDuration: clampedValue });
  };

  const handleAddClub = (club: Club) => {
    if (effectiveClubSchedules.some((cs) => cs.clubId === club.id)) return;

    const newClubSchedule: ClubScheduleSelection = {
      clubId: club.id,
      club: club,
      scheduleIds: [],
    };

    updateData({
      clubSchedules: [...effectiveClubSchedules, newClubSchedule],
    });

    fetchSchedulesForClub(club.id);
  };

  const handleRemoveClub = (clubId: string) => {
    updateData({
      clubSchedules: effectiveClubSchedules.filter(
        (cs) => cs.clubId !== clubId,
      ),
    });
  };

  const handleToggleSchedule = (clubId: string, scheduleId: string) => {
    const clubScheduleIndex = effectiveClubSchedules.findIndex(
      (cs) => cs.clubId === clubId,
    );
    if (clubScheduleIndex === -1) return;

    const currentSchedules =
      effectiveClubSchedules[clubScheduleIndex].scheduleIds;
    const isSelected = currentSchedules.includes(scheduleId);

    // Defensive guard: don't allow selecting invalid schedules even if they slip into UI.
    if (!isSelected && signupDeadlineDate) {
      const schedules = schedulesCache[clubId];
      const schedule = schedules?.find((s) => s.id === scheduleId);
      if (
        schedule &&
        !isScheduleStartAfterDeadline(schedule, signupDeadlineDate)
      ) {
        return;
      }
    }

    const newScheduleIds = isSelected
      ? currentSchedules.filter((id) => id !== scheduleId)
      : [...currentSchedules, scheduleId];

    const updatedClubSchedules = [...effectiveClubSchedules];
    updatedClubSchedules[clubScheduleIndex] = {
      ...updatedClubSchedules[clubScheduleIndex],
      scheduleIds: newScheduleIds,
    };

    updateData({ clubSchedules: updatedClubSchedules });
  };

  const handleScheduleCreated = async (
    clubId: string,
    newSchedule: Schedule,
  ) => {
    // Add to cache
    setSchedulesCache((prev) => ({
      ...prev,
      [clubId]: [...(prev[clubId] || []), newSchedule],
    }));

    // Auto-select the new schedule
    handleToggleSchedule(clubId, newSchedule.id);
  };

  // Loading state
  if (isLoadingClubs) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="mt-4 text-sm text-gray-600">Cargando clubs...</p>
      </div>
    );
  }

  // Error state
  if (clubsError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="rounded-full bg-red-100 p-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
        </div>
        <p className="mt-4 text-sm text-red-600">{clubsError}</p>
        <Button variant="outline" onClick={fetchClubs} className="mt-4 gap-2">
          <RefreshCw className="h-4 w-4" />
          Reintentar
        </Button>
      </div>
    );
  }

  // No clubs
  if (availableClubs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Building2 className="h-12 w-12 text-gray-300" />
        <h3 className="mt-4 text-lg font-medium text-gray-700">
          No tienes clubs disponibles
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          Debes tener al menos un club habilitado con canchas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Internal Step Indicator */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => setInternalStep('tournament-config')}
          className={cn(
            'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors',
            internalStep === 'tournament-config'
              ? 'bg-blue-600 text-white'
              : isTournamentConfigComplete
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
          )}
        >
          {isTournamentConfigComplete &&
            internalStep !== 'tournament-config' && (
              <Check className="h-4 w-4" />
            )}
          <Settings className="h-4 w-4" />
          <span>1. Configuración</span>
        </button>

        <ArrowRight className="h-4 w-4 text-gray-400" />

        <button
          onClick={() =>
            isTournamentConfigComplete && setInternalStep('clubs-schedules')
          }
          disabled={!isTournamentConfigComplete}
          className={cn(
            'flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors',
            internalStep === 'clubs-schedules'
              ? 'bg-blue-600 text-white'
              : !isTournamentConfigComplete
                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
          )}
        >
          <Building2 className="h-4 w-4" />
          <span>2. Clubs y Horarios</span>
          {totalSchedules > 0 && (
            <Badge variant="secondary" className="ml-1">
              {totalSchedules}
            </Badge>
          )}
        </button>
      </div>

      <Separator />

      {/* Sub-step Content */}
      {internalStep === 'tournament-config' ? (
        <TournamentConfigSection
          signupDeadlineDate={signupDeadlineDate}
          matchDuration={matchDuration}
          onSignupDeadlineChange={handleSignupDeadlineChange}
          onMatchDurationChange={handleMatchDurationChange}
          onContinue={() => setInternalStep('clubs-schedules')}
          isComplete={isTournamentConfigComplete}
        />
      ) : (
        <ClubsSchedulesSection
          availableClubs={availableClubs}
          clubSchedules={effectiveClubSchedules}
          schedulesCache={schedulesCache}
          loadingClubSchedules={loadingClubSchedules}
          signupDeadlineDate={signupDeadlineDate}
          sportUid={draft?.sport?.sport?.uid}
          onAddClub={handleAddClub}
          onRemoveClub={handleRemoveClub}
          onToggleSchedule={handleToggleSchedule}
          onScheduleCreated={handleScheduleCreated}
          onFetchSchedules={fetchSchedulesForClub}
          onBack={() => setInternalStep('tournament-config')}
        />
      )}
    </div>
  );
}

// ============================================================================
// SUB-STEP 1: TOURNAMENT CONFIGURATION
// ============================================================================

interface TournamentConfigSectionProps {
  signupDeadlineDate: Date | null;
  matchDuration: number;
  onSignupDeadlineChange: (value: Date | null) => void;
  onMatchDurationChange: (value: number) => void;
  onContinue: () => void;
  isComplete: boolean;
}

function TournamentConfigSection({
  signupDeadlineDate,
  matchDuration,
  onSignupDeadlineChange,
  onMatchDurationChange,
  onContinue,
  isComplete,
}: TournamentConfigSectionProps) {
  const minDate = new Date();
  minDate.setHours(0, 0, 0, 0);
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">
          Configuración del Torneo
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Define la configuración general que aplica a todo el torneo
        </p>
      </div>

      {/* Signup Deadline */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-5 w-5 text-blue-600" />
            Fecha de cierre de inscripción
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="signup-deadline">Fecha límite *</Label>
              <DateInputWithCalendar
                id="signup-deadline"
                value={signupDeadlineDate}
                onChange={onSignupDeadlineChange}
                minDate={minDate}
                className="max-w-xs"
                placeholder="dd/mm/aaaa"
              />
            </div>
            <p className="text-sm text-gray-500">
              Los equipos podrán inscribirse hasta esta fecha. Después de esta
              fecha, las inscripciones se cerrarán automáticamente.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Match Duration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Clock className="h-5 w-5 text-blue-600" />
            Duración de partidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                type="number"
                min={30}
                max={120}
                step={15}
                value={matchDuration}
                onChange={(e) =>
                  onMatchDurationChange(parseInt(e.target.value) || 60)
                }
                className="w-24"
              />
              <span className="text-gray-600">minutos</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {[30, 45, 60, 75, 90].map((duration) => (
                <button
                  key={duration}
                  type="button"
                  onClick={() => onMatchDurationChange(duration)}
                  className={cn(
                    'rounded-full px-4 py-2 text-sm font-medium transition-colors',
                    matchDuration === duration
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                  )}
                >
                  {duration} min
                </button>
              ))}
            </div>

            <p className="text-sm text-gray-500">
              Esta duración se usará para calcular los horarios disponibles en
              cada cancha. La duración típica es de 60-90 minutos.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Continue Button */}
      <div className="flex justify-end pt-4">
        <Button
          onClick={onContinue}
          disabled={!isComplete}
          className="gap-2"
          size="lg"
        >
          Continuar a Clubs y Horarios
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {!isComplete && (
        <p className="text-center text-sm text-amber-600">
          Completa la fecha de cierre de inscripción para continuar
        </p>
      )}
    </div>
  );
}

// ============================================================================
// SUB-STEP 2: CLUBS & SCHEDULES
// ============================================================================

interface ClubsSchedulesSectionProps {
  availableClubs: Club[];
  clubSchedules: ClubScheduleSelection[];
  schedulesCache: Record<string, Schedule[]>;
  loadingClubSchedules: string | null;
  signupDeadlineDate: Date | null;
  sportUid?: string | null;
  onAddClub: (club: Club) => void;
  onRemoveClub: (clubId: string) => void;
  onToggleSchedule: (clubId: string, scheduleId: string) => void;
  onScheduleCreated: (clubId: string, schedule: Schedule) => void;
  onFetchSchedules: (clubId: string) => void;
  onBack: () => void;
}

function ClubsSchedulesSection({
  availableClubs,
  clubSchedules,
  schedulesCache,
  loadingClubSchedules,
  signupDeadlineDate,
  sportUid,
  onAddClub,
  onRemoveClub,
  onToggleSchedule,
  onScheduleCreated,
  onFetchSchedules,
  onBack,
}: ClubsSchedulesSectionProps) {
  const [showAddClub, setShowAddClub] = useState(clubSchedules.length === 0);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter available clubs (not already added, and with courts for this sport)
  const unaddedClubs = availableClubs.filter((club) => {
    if (clubSchedules.some((cs) => cs.clubId === club.id)) return false;
    if (sportUid && getCourtsCountForSport(club, sportUid) < 1) return false;
    return true;
  });

  const filteredClubs = unaddedClubs.filter((club) =>
    club.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleAddClub = (club: Club) => {
    onAddClub(club);
    setShowAddClub(false);
    setSearchQuery('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Clubs y Horarios
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Agrega clubs y selecciona los horarios disponibles para el torneo
          </p>
        </div>
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Volver a configuración
        </Button>
      </div>

      {/* Selected Clubs */}
      {clubSchedules.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-gray-700">
              Clubs seleccionados ({clubSchedules.length})
            </h3>
          </div>

          <div className="space-y-3">
            {clubSchedules.map((cs) => (
              <ClubCard
                key={cs.clubId}
                clubSchedule={cs}
                schedules={schedulesCache[cs.clubId] || []}
                isLoading={loadingClubSchedules === cs.clubId}
                signupDeadlineDate={signupDeadlineDate}
                sportUid={sportUid}
                onToggleSchedule={(scheduleId) =>
                  onToggleSchedule(cs.clubId, scheduleId)
                }
                onRemove={() => onRemoveClub(cs.clubId)}
                onScheduleCreated={(schedule) =>
                  onScheduleCreated(cs.clubId, schedule)
                }
                onLoadSchedules={() => onFetchSchedules(cs.clubId)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add Club Section */}
      {showAddClub || clubSchedules.length === 0 ? (
        <Card className="border-2 border-dashed border-blue-300 bg-blue-50/50">
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-blue-600" />
                  <h3 className="font-medium text-blue-900">Agregar club</h3>
                </div>
                {clubSchedules.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddClub(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar club..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white pl-10"
                />
              </div>

              {/* Club List */}
              {unaddedClubs.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">
                  Ya agregaste todos tus clubs disponibles
                </p>
              ) : filteredClubs.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">
                  No se encontraron clubs con "{searchQuery}"
                </p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {filteredClubs.map((club) => (
                    <button
                      key={club.id}
                      onClick={() => handleAddClub(club)}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition-all hover:border-blue-300 hover:shadow-md"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                        <Building2 className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-gray-900">
                          {club.name}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {sportUid ? getCourtsCountForSport(club, sportUid) : (club.courts?.length || 0)} canchas
                        </p>
                      </div>
                      <Plus className="h-5 w-5 text-blue-600" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Button
          variant="outline"
          onClick={() => setShowAddClub(true)}
          className="w-full gap-2 border-dashed"
          disabled={unaddedClubs.length === 0}
        >
          <Plus className="h-4 w-4" />
          {unaddedClubs.length === 0
            ? 'Ya agregaste todos tus clubs'
            : 'Agregar otro club'}
        </Button>
      )}

      {/* Summary with expandable details */}
      {clubSchedules.length > 0 && (
        <SummaryCard
          clubSchedules={clubSchedules}
          schedulesCache={schedulesCache}
        />
      )}
    </div>
  );
}

// ============================================================================
// SUMMARY CARD - Expandable summary with all time blocks
// ============================================================================

interface SummaryCardProps {
  clubSchedules: ClubScheduleSelection[];
  schedulesCache: Record<string, Schedule[]>;
}

function SummaryCard({ clubSchedules, schedulesCache }: SummaryCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedScheduleIds, setExpandedScheduleIds] = useState<Set<string>>(
    new Set(),
  );

  // Calculate totals
  const totalSchedules = clubSchedules.reduce(
    (acc, cs) => acc + cs.scheduleIds.length,
    0,
  );
  const totalTimeBlocks = clubSchedules.reduce((acc, cs) => {
    const schedules = schedulesCache[cs.clubId] || [];
    return (
      acc +
      cs.scheduleIds.reduce((schedAcc, scheduleId) => {
        const schedule = schedules.find((s) => s.id === scheduleId);
        return schedAcc + (schedule?.timeBlocks.length || 0);
      }, 0)
    );
  }, 0);
  const totalPartidos = clubSchedules.reduce((acc, cs) => {
    const schedules = schedulesCache[cs.clubId] || [];
    return (
      acc +
      cs.scheduleIds.reduce((schedAcc, scheduleId) => {
        const schedule = schedules.find((s) => s.id === scheduleId);
        if (!schedule) return schedAcc;
        return (
          schedAcc +
          schedule.timeBlocks.reduce((blockAcc, b) => blockAcc + b.courtCount, 0)
        );
      }, 0)
    );
  }, 0);

  const hasClubsWithoutSchedules = clubSchedules.some(
    (cs) => cs.scheduleIds.length === 0,
  );

  // Format date helper
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const toggleScheduleExpanded = (scheduleId: string) => {
    setExpandedScheduleIds((prev) => {
      const next = new Set(prev);
      if (next.has(scheduleId)) {
        next.delete(scheduleId);
      } else {
        next.add(scheduleId);
      }
      return next;
    });
  };

  const INITIAL_BLOCKS_SHOWN = 8;

  return (
    <Card className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
      <CardContent className="p-0">
        {/* Header - clickable */}
        <div
          className="flex cursor-pointer items-center justify-between p-4"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600 shadow-lg">
              <CalendarDays className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900">
                {clubSchedules.length} club
                {clubSchedules.length !== 1 ? 's' : ''} · {totalSchedules}{' '}
                bloque{totalSchedules !== 1 ? 's' : ''} de horario
              </p>
              <p className="text-sm text-gray-600">
                {totalTimeBlocks} horarios totales disponibles
                {totalPartidos > 0 && (
                  <> · {totalPartidos} partido{totalPartidos !== 1 ? 's' : ''}</>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {hasClubsWithoutSchedules && (
              <Badge
                variant="outline"
                className="border-amber-300 bg-amber-50 text-amber-700"
              >
                Algunos clubs sin bloques de horario
              </Badge>
            )}
            <Button variant="ghost" size="sm" className="gap-1">
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Ocultar detalle
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Ver detalle
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Expanded content - Time blocks grouped by club */}
        {isExpanded && (
          <div className="border-t border-blue-200 bg-white p-4">
            <div className="space-y-6">
              {clubSchedules.map((cs) => {
                const schedules = schedulesCache[cs.clubId] || [];
                const selectedSchedules = schedules.filter((s) =>
                  cs.scheduleIds.includes(s.id),
                );

                if (selectedSchedules.length === 0) {
                  return (
                    <div
                      key={cs.clubId}
                      className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                    >
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-amber-600" />
                        <span className="font-medium text-amber-800">
                          {cs.club.name}
                        </span>
                        <span className="text-sm text-amber-600">
                          — Sin bloques de horario seleccionados
                        </span>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={cs.clubId} className="space-y-3">
                    {/* Club header */}
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold text-gray-900">
                        {cs.club.name}
                      </h4>
                      <Badge variant="secondary" className="ml-2">
                        {selectedSchedules.length} bloque
                        {selectedSchedules.length !== 1 ? 's' : ''} de horario
                      </Badge>
                    </div>

                    {/* Schedules for this club */}
                    <div className="ml-7 space-y-3">
                      {selectedSchedules.map((schedule) => {
                        const isScheduleExpanded = expandedScheduleIds.has(
                          schedule.id,
                        );
                        const blocksToShow = isScheduleExpanded
                          ? schedule.timeBlocks
                          : schedule.timeBlocks.slice(0, INITIAL_BLOCKS_SHOWN);
                        const hasMoreBlocks =
                          schedule.timeBlocks.length > INITIAL_BLOCKS_SHOWN;
                        const hiddenCount =
                          schedule.timeBlocks.length - INITIAL_BLOCKS_SHOWN;

                        // Calculate date range for this schedule
                        const scheduleDates = [
                          ...new Set(schedule.timeBlocks.map((b) => b.date)),
                        ].sort();
                        const scheduleDateRange =
                          scheduleDates.length > 0
                            ? scheduleDates.length === 1
                              ? formatDate(scheduleDates[0])
                              : `${formatDate(scheduleDates[0])} - ${formatDate(scheduleDates[scheduleDates.length - 1])}`
                            : '';

                        return (
                          <div
                            key={schedule.id}
                            className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="mb-2 flex items-center justify-between">
                              <div>
                                <span className="font-medium text-gray-800">
                                  {schedule.title}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {scheduleDateRange}
                                </p>
                              </div>
                              <Badge variant="outline">
                                {schedule.timeBlocks.length} horarios
                              </Badge>
                            </div>

                            {/* Time blocks grid */}
                            <div className="grid gap-1.5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                              {blocksToShow.map((block) => (
                                <div
                                  key={block.id}
                                  className="flex items-center justify-between rounded bg-white px-2 py-1.5 text-xs shadow-sm"
                                >
                                  <span className="font-medium text-gray-700">
                                    {formatDate(block.date)}
                                  </span>
                                  <span className="text-gray-500">
                                    {block.startTime} - {block.endTime}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className="ml-1 h-5 px-1.5 text-[10px]"
                                  >
                                    {block.courtCount}
                                  </Badge>
                                </div>
                              ))}
                            </div>

                            {/* Show more/less button */}
                            {hasMoreBlocks && (
                              <button
                                onClick={() =>
                                  toggleScheduleExpanded(schedule.id)
                                }
                                className="mt-2 flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
                              >
                                {isScheduleExpanded ? (
                                  <>
                                    <ChevronUp className="h-3 w-3" />
                                    Mostrar menos
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-3 w-3" />
                                    Ver {hiddenCount} horarios más
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================================
// SCHEDULE ITEM EXPANDABLE - Shows schedule with expandable time blocks
// ============================================================================

interface ScheduleItemExpandableProps {
  schedule: Schedule;
  isSelected: boolean;
  onToggle: () => void;
}

function ScheduleItemExpandable({
  schedule,
  isSelected,
  onToggle,
}: ScheduleItemExpandableProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllDates, setShowAllDates] = useState(false);

  const INITIAL_DATES_SHOWN = 5;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatDateShort = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
    });
  };

  // Group time blocks by date
  const blocksByDate = schedule.timeBlocks.reduce(
    (acc, block) => {
      if (!acc[block.date]) {
        acc[block.date] = [];
      }
      acc[block.date].push(block);
      return acc;
    },
    {} as Record<string, TimeBlock[]>,
  );

  const dates = Object.keys(blocksByDate).sort();
  const datesToShow = showAllDates
    ? dates
    : dates.slice(0, INITIAL_DATES_SHOWN);
  const hasMoreDates = dates.length > INITIAL_DATES_SHOWN;
  const hiddenDatesCount = dates.length - INITIAL_DATES_SHOWN;

  // Calculate date range
  const dateRange =
    dates.length > 0
      ? dates.length === 1
        ? formatDateShort(dates[0])
        : `${formatDateShort(dates[0])} - ${formatDateShort(dates[dates.length - 1])}`
      : '';

  const totalCourts = schedule.timeBlocks.reduce(
    (sum, b) => sum + b.courtCount,
    0,
  );

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        isSelected
          ? 'border-blue-300 bg-blue-50'
          : 'border-gray-200 hover:border-gray-300',
      )}
    >
      {/* Main row */}
      <div className="flex items-center gap-3 p-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggle}
          className="h-5 w-5"
          onClick={(e) => e.stopPropagation()}
        />

        <div
          className="flex flex-1 cursor-pointer items-center justify-between"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex-1">
            <p
              className={cn(
                'font-medium',
                isSelected ? 'text-blue-900' : 'text-gray-900',
              )}
            >
              {schedule.title}
            </p>
            <p className="text-sm text-gray-500">
              {schedule.timeBlocks.length} horarios · {totalCourts} partido
              {totalCourts !== 1 ? 's' : ''} · {dates.length} día
              {dates.length !== 1 ? 's' : ''} · {dateRange}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isSelected && <Badge className="bg-blue-600">Seleccionado</Badge>}
            <Button variant="ghost" size="icon" className="h-8 w-8">
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Expanded time blocks */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-white p-3">
          <div className="space-y-3">
            {datesToShow.map((date) => {
              const blocks = blocksByDate[date];
              return (
                <div key={date} className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-600">
                    {formatDate(date)}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {blocks.map((block) => (
                      <div
                        key={block.id}
                        className={cn(
                          'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                          isSelected
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-700',
                        )}
                      >
                        <Clock className="h-3 w-3" />
                        <span>
                          {block.startTime} - {block.endTime}
                        </span>
                        <Badge
                          variant="secondary"
                          className={cn(
                            'h-4 px-1 text-[10px]',
                            isSelected && 'bg-blue-200',
                          )}
                        >
                          {block.courtCount} partido
                          {block.courtCount === 1 ? '' : 's'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Show more/less dates button */}
            {hasMoreDates && (
              <button
                onClick={() => setShowAllDates(!showAllDates)}
                className="flex w-full items-center justify-center gap-1 rounded-md py-2 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-50"
              >
                {showAllDates ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Mostrar menos días
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    Ver {hiddenDatesCount} días más
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// CLUB CARD - Expandable card for each club
// ============================================================================

interface ClubCardProps {
  clubSchedule: ClubScheduleSelection;
  schedules: Schedule[];
  isLoading: boolean;
  signupDeadlineDate: Date | null;
  sportUid?: string | null;
  onToggleSchedule: (scheduleId: string) => void;
  onRemove: () => void;
  onScheduleCreated: (schedule: Schedule) => void;
  onLoadSchedules: () => void;
}

function ClubCard({
  clubSchedule,
  schedules,
  isLoading,
  signupDeadlineDate,
  sportUid,
  onToggleSchedule,
  onRemove,
  onScheduleCreated,
  onLoadSchedules,
}: ClubCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const club = clubSchedule.club;
  const selectedCount = clubSchedule.scheduleIds.length;
  const selectedSchedules = schedules.filter((s) =>
    clubSchedule.scheduleIds.includes(s.id),
  );
  const totalTimeBlocks = selectedSchedules.reduce(
    (acc, s) => acc + s.timeBlocks.length,
    0,
  );

  // Load schedules when expanded
  useEffect(() => {
    if (isExpanded && schedules.length === 0 && !isLoading) {
      onLoadSchedules();
    }
  }, [isExpanded, schedules.length, isLoading, onLoadSchedules]);

  const maxCourts =
    sportUid != null && String(sportUid).trim()
      ? getCourtsCountForSport(club, sportUid)
      : club.courts?.length ?? 4;

  const clubForCreate: ClubWithSchedules = {
    id: club.id,
    name: club.name,
    address:
      club.address?.street && club.address?.city
        ? `${club.address.street}, ${club.address.city}`
        : club.address?.street || '',
    maxCourts,
  };

  const handleScheduleCreated = async (scheduleData: CreateScheduleRequest) => {
    try {
      const createdSchedule = await createSchedule(club.id, scheduleData);
      const uiSchedule = transformApiScheduleToUI(createdSchedule);
      onScheduleCreated(uiSchedule);
      setShowCreateForm(false);
    } catch (err) {
      console.error('Error creating schedule:', err);
      handleGlobalError(err, 'Error', 'No se pudo crear el bloque de horario');
    }
  };

  return (
    <Card
      className={cn(
        'overflow-hidden transition-all',
        selectedCount > 0 && 'border-blue-200 bg-blue-50/30',
      )}
    >
      {/* Header - Always visible */}
      <div
        className="flex cursor-pointer items-center justify-between p-4 hover:bg-gray-50/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg',
              selectedCount > 0 ? 'bg-blue-100' : 'bg-gray-100',
            )}
          >
            <Building2
              className={cn(
                'h-5 w-5',
                selectedCount > 0 ? 'text-blue-600' : 'text-gray-600',
              )}
            />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{club.name}</h4>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {selectedCount > 0 ? (
                <>
                  <Check className="h-3 w-3 text-green-600" />
                  <span className="text-green-700">
                    {selectedCount} bloque{selectedCount !== 1 ? 's' : ''} de
                    horario · {totalTimeBlocks} horarios
                  </span>
                </>
              ) : (
                <span className="text-amber-600">
                  Sin bloques de horario seleccionados
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-red-500 hover:bg-red-50 hover:text-red-600"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-white p-4">
          {showCreateForm ? (
            <CreateScheduleInline
              club={clubForCreate}
              onCreated={handleScheduleCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          ) : (
            <>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-sm text-gray-500">
                    Cargando bloques de horario...
                  </span>
                </div>
              ) : schedules.length === 0 ? (
                <div className="py-8 text-center">
                  <Calendar className="mx-auto h-10 w-10 text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">
                    No hay bloques de horario para este club
                  </p>
                  <Button
                    className="mt-4"
                    size="sm"
                    onClick={() => setShowCreateForm(true)}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Crear primer bloque de horario
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Schedule list */}
                  <div className="space-y-2">
                    {(() => {
                      const eligible = schedules.filter((s) =>
                        isScheduleStartAfterDeadline(s, signupDeadlineDate),
                      );

                      if (eligible.length === 0 && signupDeadlineDate) {
                        return (
                          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-center text-sm text-amber-800">
                            No hay bloques disponibles posteriores a la fecha de
                            cierre de inscripción.
                          </p>
                        );
                      }

                      return eligible.map((schedule) => (
                        <ScheduleItemExpandable
                          key={schedule.id}
                          schedule={schedule}
                          isSelected={clubSchedule.scheduleIds.includes(
                            schedule.id,
                          )}
                          onToggle={() => onToggleSchedule(schedule.id)}
                        />
                      ));
                    })()}
                  </div>

                  {/* Create new button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCreateForm(true)}
                    className="w-full gap-2 border-dashed"
                  >
                    <Plus className="h-4 w-4" />
                    Crear nuevo bloque de horario
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// CREATE SCHEDULE INLINE
// ============================================================================

interface TimeSlotSelection {
  [dateKey: string]: {
    [timeKey: string]: number;
  };
}

interface CreateScheduleInlineProps {
  club: ClubWithSchedules;
  onCreated: (scheduleData: CreateScheduleRequest) => void;
  onCancel: () => void;
}

function CreateScheduleInline({
  club,
  onCreated,
  onCancel,
}: CreateScheduleInlineProps) {
  const [title, setTitle] = useState('');
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({ from: undefined, to: undefined });
  const [timeSlots, setTimeSlots] = useState<TimeSlotSelection>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MATCH_DURATION = 60;
  const SLOT_INTERVAL = 30;

  const getDatesInRange = (start: Date, end: Date): Date[] => {
    const dates: Date[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const generateTimeSlots = (): {
    startTime: string;
    endTime: string;
    display: string;
  }[] => {
    const slots: { startTime: string; endTime: string; display: string }[] = [];
    const startHour = 8;
    const endHour = 23;

    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += SLOT_INTERVAL) {
        const startTime = new Date();
        startTime.setHours(hour, minute, 0, 0);

        const endTime = new Date(startTime);
        endTime.setMinutes(endTime.getMinutes() + MATCH_DURATION);

        if (
          endTime.getHours() <= endHour ||
          (endTime.getHours() === endHour + 1 && endTime.getMinutes() === 0)
        ) {
          const startStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const endStr = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;

          slots.push({
            startTime: startStr,
            endTime: endStr,
            display: `${startStr} - ${endStr}`,
          });
        }
      }
    }
    return slots;
  };

  const availableSlots = generateTimeSlots();

  const formatDateDisplay = (date: Date): string => {
    return date.toLocaleDateString('es-AR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatDateKey = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const getSlotCount = (dateKey: string, timeKey: string): number => {
    return timeSlots[dateKey]?.[timeKey] ?? 0;
  };

  const handleSlotClick = (dateKey: string, timeKey: string) => {
    const currentCount = getSlotCount(dateKey, timeKey);
    const maxCourts = club.maxCourts;

    if (currentCount < maxCourts) {
      setTimeSlots((prev) => ({
        ...prev,
        [dateKey]: {
          ...prev[dateKey],
          [timeKey]: currentCount + 1,
        },
      }));
    } else {
      // Reset to 0
      setTimeSlots((prev) => {
        const newDateSlots = { ...prev[dateKey] };
        delete newDateSlots[timeKey];
        if (Object.keys(newDateSlots).length === 0) {
          const newSlots = { ...prev };
          delete newSlots[dateKey];
          return newSlots;
        }
        return { ...prev, [dateKey]: newDateSlots };
      });
    }
  };

  const totalSlots = Object.values(timeSlots).reduce(
    (acc, dateSlots) => acc + Object.keys(dateSlots).length,
    0,
  );

  const handleCreate = async () => {
    setIsSubmitting(true);
    try {
      const scheduleEntries = transformTimeSlotsToApiFormat(
        timeSlots,
        availableSlots,
      );
      const scheduleData: CreateScheduleRequest = {
        name: title,
        schedule: scheduleEntries,
      };
      await onCreated(scheduleData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = title.trim() && totalSlots > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">
          Crear nuevo bloque de horario
        </h4>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="schedule-title">Nombre del bloque de horario *</Label>
        <Input
          id="schedule-title"
          placeholder="Ej: Fines de semana - Febrero"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      {/* Date Range */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Fecha desde *</Label>
          <Input
            type="date"
            value={dateRange.from ? formatDateKey(dateRange.from) : ''}
            onChange={(e) => {
              const date = e.target.value
                ? new Date(e.target.value + 'T00:00:00')
                : undefined;
              setDateRange((prev) => ({ ...prev, from: date }));
            }}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div className="space-y-2">
          <Label>Fecha hasta *</Label>
          <Input
            type="date"
            value={dateRange.to ? formatDateKey(dateRange.to) : ''}
            onChange={(e) => {
              const date = e.target.value
                ? new Date(e.target.value + 'T00:00:00')
                : undefined;
              setDateRange((prev) => ({ ...prev, to: date }));
            }}
            min={
              dateRange.from
                ? formatDateKey(dateRange.from)
                : new Date().toISOString().split('T')[0]
            }
          />
        </div>
      </div>

      {/* Time Slots */}
      {dateRange.from && dateRange.to && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Horarios disponibles</Label>
            <Badge variant="secondary">{totalSlots} seleccionados</Badge>
          </div>
          <p className="text-xs text-gray-500">
            Clic para agregar canchas (máx {club.maxCourts}). Clic en
            seleccionado para quitar.
          </p>

          <div className="max-h-64 space-y-4 overflow-y-auto rounded-lg border border-gray-200 p-3">
            {getDatesInRange(dateRange.from, dateRange.to).map((date) => {
              const dateKey = formatDateKey(date);
              return (
                <div key={dateKey} className="space-y-2">
                  <p className="text-sm font-medium capitalize text-gray-700">
                    {formatDateDisplay(date)}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {availableSlots.slice(0, 16).map((slot) => {
                      const count = getSlotCount(dateKey, slot.startTime);
                      return (
                        <button
                          key={`${dateKey}-${slot.startTime}`}
                          type="button"
                          onClick={() =>
                            handleSlotClick(dateKey, slot.startTime)
                          }
                          className={cn(
                            'relative rounded px-2 py-1 text-xs transition-colors',
                            count > 0
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                          )}
                        >
                          {slot.startTime}
                          {count > 0 && (
                            <span className="ml-1 rounded-full bg-white px-1 text-[10px] font-bold text-blue-600">
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          size="sm"
          onClick={handleCreate}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Creando...
            </>
          ) : (
            <>
              <Check className="mr-1 h-4 w-4" />
              Crear ({totalSlots} horarios)
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

export default ScheduleSelectionStep;
